import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createStripeClient, type StripeEnv, getStripeErrorMessage } from "@/lib/stripe.server";

async function ensureOwner(supabase: any, userId: string) {
  const { data } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .in("role", ["owner", "admin"])
    .maybeSingle();
  if (!data) throw new Error("forbidden");
}

const CodeSchema = z
  .string()
  .min(3)
  .max(32)
  .regex(/^[A-Z0-9_-]+$/i, "Só letras, dígitos, - ou _");
const SlugSchema = z
  .string()
  .min(3)
  .max(48)
  .regex(/^[A-Z0-9_-]+$/i, "Só letras, dígitos, - ou _");

const UpsertSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().min(1).max(120),
  email: z.string().trim().email().max(255),
  code: CodeSchema,
  ref_slug: SlugSchema,
  discount_percent: z.number().min(0).max(100),
  commission_percent: z.number().min(0).max(100),
  starts_at: z.string().datetime().nullable().optional(),
  ends_at: z.string().datetime().nullable().optional(),
  active: z.boolean().default(true),
  notes: z.string().max(1000).nullable().optional(),
  syncStripe: z.enum(["sandbox", "live", "both", "none"]).default("both"),
});

async function syncStripeCoupon(
  env: StripeEnv,
  aff: {
    code: string;
    discount_percent: number;
    ends_at?: string | null;
    couponId?: string | null;
    promoCodeId?: string | null;
  },
) {
  const stripe = createStripeClient(env);
  let couponId = aff.couponId ?? null;
  // Coupons are largely immutable; if percent changed we create a new one.
  if (!couponId) {
    const c = await stripe.coupons.create({
      percent_off: aff.discount_percent,
      duration: "forever",
      name: `Routify · ${aff.code}`,
      metadata: { affiliate_code: aff.code },
    });
    couponId = c.id;
  } else {
    try {
      const existing = await stripe.coupons.retrieve(couponId);
      if (Math.round(Number(existing.percent_off ?? 0)) !== Math.round(aff.discount_percent)) {
        const c = await stripe.coupons.create({
          percent_off: aff.discount_percent,
          duration: "forever",
          name: `Routify · ${aff.code}`,
          metadata: { affiliate_code: aff.code },
        });
        couponId = c.id;
      }
    } catch {
      const c = await stripe.coupons.create({
        percent_off: aff.discount_percent,
        duration: "forever",
        name: `Routify · ${aff.code}`,
        metadata: { affiliate_code: aff.code },
      });
      couponId = c.id;
    }
  }

  // Promotion code: create if missing, otherwise ensure active/expiry.
  // Dahlia API: use `promotion: { type: 'coupon', coupon }` instead of top-level `coupon`.
  let promoId = aff.promoCodeId ?? null;
  const promoBase: any = {
    promotion: { type: "coupon", coupon: couponId },
    code: aff.code,
    metadata: { affiliate_code: aff.code },
  };

  if (!promoId) {
    const p = await stripe.promotionCodes.create({
      ...promoBase,
      ...(aff.ends_at ? { expires_at: Math.floor(new Date(aff.ends_at).getTime() / 1000) } : {}),
    });
    promoId = p.id;
  } else {
    try {
      await stripe.promotionCodes.update(promoId, { active: true });
    } catch {
      const p = await stripe.promotionCodes.create(promoBase);
      promoId = p.id;
    }
  }

  return { couponId, promoId };
}

export const listAffiliates = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureOwner(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("affiliates")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

// ---- Owner tools: link / unlink / invite affiliate account ----

async function findUserIdByEmail(supabaseAdmin: any, email: string): Promise<string | null> {
  const target = email.trim().toLowerCase();
  if (!target) return null;
  const perPage = 200;
  for (let page = 1; page <= 20; page++) {
    const { data, error } = await supabaseAdmin.auth.admin.listUsers({ page, perPage });
    if (error) throw new Error(error.message);
    const found = data.users.find((u: any) => (u.email ?? "").toLowerCase() === target);
    if (found) return found.id;
    if (data.users.length < perPage) break;
  }
  return null;
}

export const linkAffiliateAccount = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ affiliateId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await ensureOwner(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: aff } = await supabaseAdmin.from("affiliates").select("id, email, user_id").eq("id", data.affiliateId).maybeSingle();
    if (!aff) return { ok: false as const, error: "not_found" };
    if (aff.user_id) return { ok: true as const, alreadyLinked: true, userId: aff.user_id };
    const userId = await findUserIdByEmail(supabaseAdmin, aff.email);
    if (!userId) return { ok: false as const, error: "no_account" };
    const { error } = await supabaseAdmin.from("affiliates").update({ user_id: userId, updated_at: new Date().toISOString() }).eq("id", aff.id);
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const, userId };
  });

export const unlinkAffiliateAccount = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ affiliateId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await ensureOwner(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("affiliates").update({ user_id: null, updated_at: new Date().toISOString() }).eq("id", data.affiliateId);
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const };
  });

export const sendAffiliateInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ affiliateId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await ensureOwner(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: aff } = await supabaseAdmin.from("affiliates").select("id, email, name, user_id").eq("id", data.affiliateId).maybeSingle();
    if (!aff) return { ok: false as const, error: "not_found" };

    const origin = process.env.SITE_URL || "https://www.routify-tour.com";
    const redirectTo = `${origin}/affiliate`;

    if (aff.user_id) {
      const { error } = await supabaseAdmin.auth.admin.generateLink({
        type: "magiclink",
        email: aff.email,
        options: { redirectTo },
      });
      if (error) return { ok: false as const, error: error.message };
      return { ok: true as const, mode: "magiclink" as const };
    }

    const { data: inv, error } = await supabaseAdmin.auth.admin.inviteUserByEmail(aff.email, {
      redirectTo,
      data: { full_name: aff.name, is_affiliate: true },
    });
    if (error) return { ok: false as const, error: error.message };
    if (inv?.user?.id) {
      await supabaseAdmin
        .from("affiliates")
        .update({ user_id: inv.user.id, updated_at: new Date().toISOString() })
        .eq("id", aff.id);
    }
    return { ok: true as const, mode: "invite" as const };
  });

// Lightweight badge used by the site header to decide whether to show
// the "Painel de Afiliado" entry. Also lazily links the account on first
// authenticated visit if only the email matches (triggers grant the role).
export const getMyAffiliateBadge = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: linked } = await supabaseAdmin
      .from("affiliates")
      .select("id, code, ref_slug, active")
      .eq("user_id", context.userId)
      .maybeSingle();
    if (linked) return { isAffiliate: true as const, code: linked.code, refSlug: linked.ref_slug, active: linked.active };

    const { data: userRes } = await supabaseAdmin.auth.admin.getUserById(context.userId);
    const email = userRes?.user?.email?.toLowerCase();
    if (!email) return { isAffiliate: false as const };
    const { data: byEmail } = await supabaseAdmin
      .from("affiliates")
      .select("id, code, ref_slug, active, user_id")
      .eq("email", email)
      .maybeSingle();
    if (!byEmail) return { isAffiliate: false as const };
    if (!byEmail.user_id) {
      await supabaseAdmin
        .from("affiliates")
        .update({ user_id: context.userId, updated_at: new Date().toISOString() })
        .eq("id", byEmail.id);
    }
    return { isAffiliate: true as const, code: byEmail.code, refSlug: byEmail.ref_slug, active: byEmail.active };
  });

export const upsertAffiliate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => UpsertSchema.parse(d))
  .handler(async ({ data, context }) => {
    await ensureOwner(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Load existing (for stripe IDs) if editing
    let existing: any = null;
    if (data.id) {
      const { data: row } = await supabaseAdmin.from("affiliates").select("*").eq("id", data.id).maybeSingle();
      existing = row;
    }

    const stripeIds: any = {
      stripe_coupon_id_sandbox: existing?.stripe_coupon_id_sandbox ?? null,
      stripe_promotion_code_id_sandbox: existing?.stripe_promotion_code_id_sandbox ?? null,
      stripe_coupon_id_live: existing?.stripe_coupon_id_live ?? null,
      stripe_promotion_code_id_live: existing?.stripe_promotion_code_id_live ?? null,
    };

    const doSandbox = data.syncStripe === "sandbox" || data.syncStripe === "both";
    const doLive = data.syncStripe === "live" || data.syncStripe === "both";

    try {
      if (doSandbox) {
        const r = await syncStripeCoupon("sandbox", {
          code: data.code,
          discount_percent: data.discount_percent,
          ends_at: data.ends_at ?? null,
          couponId: stripeIds.stripe_coupon_id_sandbox,
          promoCodeId: stripeIds.stripe_promotion_code_id_sandbox,
        });
        stripeIds.stripe_coupon_id_sandbox = r.couponId;
        stripeIds.stripe_promotion_code_id_sandbox = r.promoId;
      }
      if (doLive) {
        try {
          const r = await syncStripeCoupon("live", {
            code: data.code,
            discount_percent: data.discount_percent,
            ends_at: data.ends_at ?? null,
            couponId: stripeIds.stripe_coupon_id_live,
            promoCodeId: stripeIds.stripe_promotion_code_id_live,
          });
          stripeIds.stripe_coupon_id_live = r.couponId;
          stripeIds.stripe_promotion_code_id_live = r.promoId;
        } catch (e) {
          // Live may not be configured yet — non-fatal.
          console.warn("Stripe live sync skipped:", getStripeErrorMessage(e));
        }
      }
    } catch (e) {
      return { ok: false, error: getStripeErrorMessage(e) };
    }

    // Auto-link to an existing auth account by email (owner-driven association).
    let linkedUserId: string | null = existing?.user_id ?? null;
    try {
      if (!linkedUserId) {
        linkedUserId = await findUserIdByEmail(supabaseAdmin, data.email);
      }
    } catch {
      // non-fatal — link can be redone from the panel
    }

    const row = {
      ...(data.id ? { id: data.id } : {}),
      name: data.name,
      email: data.email.toLowerCase(),
      code: data.code.toUpperCase(),
      ref_slug: data.ref_slug.toUpperCase(),
      discount_percent: data.discount_percent,
      commission_percent: data.commission_percent,
      starts_at: data.starts_at ?? null,
      ends_at: data.ends_at ?? null,
      active: data.active,
      notes: data.notes ?? null,
      ...stripeIds,
      ...(linkedUserId !== undefined ? { user_id: linkedUserId } : {}),
      updated_at: new Date().toISOString(),
    };

    const { data: saved, error } = await supabaseAdmin
      .from("affiliates")
      .upsert(row)
      .select("*")
      .single();
    if (error) return { ok: false, error: error.message };
    return { ok: true, affiliate: saved };
  });

export const toggleAffiliate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid(), active: z.boolean() }).parse(d))
  .handler(async ({ data, context }) => {
    await ensureOwner(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("affiliates").update({ active: data.active }).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteAffiliate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await ensureOwner(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Block delete if commissions exist to preserve financial history.
    const { count } = await supabaseAdmin
      .from("affiliate_commissions")
      .select("id", { count: "exact", head: true })
      .eq("affiliate_id", data.id);
    if ((count ?? 0) > 0) {
      return { ok: false, error: "Existem comissões associadas — desativa em vez de eliminar." };
    }
    const { error } = await supabaseAdmin.from("affiliates").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const getAffiliateStats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await ensureOwner(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [clicks, signups, purchases, commissions] = await Promise.all([
      supabaseAdmin.from("affiliate_events").select("id", { count: "exact", head: true }).eq("affiliate_id", data.id).eq("kind", "click"),
      supabaseAdmin.from("affiliate_events").select("id", { count: "exact", head: true }).eq("affiliate_id", data.id).eq("kind", "signup"),
      supabaseAdmin.from("affiliate_events").select("id", { count: "exact", head: true }).eq("affiliate_id", data.id).eq("kind", "purchase"),
      supabaseAdmin.from("affiliate_commissions").select("commission_cents,status").eq("affiliate_id", data.id),
    ]);
    const rows = (commissions.data ?? []) as { commission_cents: number; status: string }[];
    const pendingCents = rows.filter((r) => r.status === "pending").reduce((a, r) => a + r.commission_cents, 0);
    const paidCents = rows.filter((r) => r.status === "paid").reduce((a, r) => a + r.commission_cents, 0);
    return {
      clicks: clicks.count ?? 0,
      signups: signups.count ?? 0,
      purchases: purchases.count ?? 0,
      pendingCents,
      paidCents,
    };
  });

const StatusSchema = z.enum(["pending", "validating", "approved", "paid", "canceled", "void"]);

async function logAudit(
  supabaseAdmin: any,
  args: {
    commissionId?: string | null;
    affiliateId?: string | null;
    actorUserId: string;
    action: string;
    fromStatus?: string | null;
    toStatus?: string | null;
    notes?: string | null;
    metadata?: Record<string, unknown> | null;
  },
) {
  try {
    await supabaseAdmin.from("affiliate_audit_log").insert({
      commission_id: args.commissionId ?? null,
      affiliate_id: args.affiliateId ?? null,
      actor_user_id: args.actorUserId,
      action: args.action,
      from_status: args.fromStatus ?? null,
      to_status: args.toStatus ?? null,
      notes: args.notes ?? null,
      metadata: args.metadata ?? null,
    });
  } catch (e) {
    console.error("logAudit failed", e);
  }
}

export const setCommissionStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      id: z.string().uuid(),
      status: StatusSchema,
      notes: z.string().max(500).optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await ensureOwner(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: current } = await supabaseAdmin
      .from("affiliate_commissions")
      .select("id, status, affiliate_id")
      .eq("id", data.id)
      .maybeSingle();
    if (!current) return { ok: false, error: "not found" };

    const patch: any = { status: data.status, updated_at: new Date().toISOString() };
    if (data.status === "approved") patch.approved_at = new Date().toISOString();
    if (data.status === "paid") patch.paid_at = new Date().toISOString();
    if (data.status === "canceled") patch.canceled_at = new Date().toISOString();

    const { error } = await supabaseAdmin
      .from("affiliate_commissions")
      .update(patch)
      .eq("id", data.id);
    if (error) return { ok: false, error: error.message };

    await logAudit(supabaseAdmin, {
      commissionId: data.id,
      affiliateId: current.affiliate_id,
      actorUserId: context.userId,
      action: "manual_status_change",
      fromStatus: current.status,
      toStatus: data.status,
      notes: data.notes ?? null,
    });
    return { ok: true };
  });

export const listCommissions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      affiliateId: z.string().uuid().optional(),
      status: StatusSchema.optional(),
      limit: z.number().int().min(1).max(500).default(200),
    }).parse(d ?? {}),
  )
  .handler(async ({ data, context }) => {
    await ensureOwner(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin
      .from("affiliate_commissions")
      .select("*, affiliates(name, code)")
      .order("created_at", { ascending: false })
      .limit(data.limit);
    if (data.affiliateId) q = q.eq("affiliate_id", data.affiliateId);
    if (data.status) q = q.eq("status", data.status);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const listAuditLog = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      commissionId: z.string().uuid().optional(),
      affiliateId: z.string().uuid().optional(),
      limit: z.number().int().min(1).max(500).default(200),
    }).parse(d ?? {}),
  )
  .handler(async ({ data, context }) => {
    await ensureOwner(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin
      .from("affiliate_audit_log")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(data.limit);
    if (data.commissionId) q = q.eq("commission_id", data.commissionId);
    if (data.affiliateId) q = q.eq("affiliate_id", data.affiliateId);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const runAutoApprove = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureOwner(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await (supabaseAdmin.rpc as any)("affiliate_auto_approve");
    if (error) return { ok: false, error: error.message };
    return { ok: true, approved: Number(data ?? 0) };
  });

export const exportCommissionsCsv = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      affiliateId: z.string().uuid().optional(),
      status: StatusSchema.optional(),
    }).parse(d ?? {}),
  )
  .handler(async ({ data, context }) => {
    await ensureOwner(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin
      .from("affiliate_commissions")
      .select("*, affiliates(name, code, email)")
      .order("created_at", { ascending: false })
      .limit(5000);
    if (data.affiliateId) q = q.eq("affiliate_id", data.affiliateId);
    if (data.status) q = q.eq("status", data.status);
    const { data: rows, error } = await q;
    if (error) return { ok: false as const, error: error.message };

    const cols = [
      "created_at","status","affiliate_code","affiliate_name","affiliate_email","currency",
      "gross_cents","discount_cents","tax_cents","stripe_fee_cents","net_eligible_cents","commission_cents",
      "stripe_customer_id","stripe_subscription_id","stripe_payment_intent_id","stripe_charge_id",
      "stripe_session_id","stripe_invoice_id","hold_until","approved_at","paid_at","canceled_at",
      "cancel_reason","self_referral","environment",
    ];
    const esc = (v: any) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [cols.join(",")];
    for (const r of rows ?? []) {
      const aff = (r as any).affiliates ?? {};
      lines.push([
        r.created_at, r.status, r.affiliate_code, aff.name, aff.email, r.currency,
        r.gross_cents, r.discount_cents, r.tax_cents, r.stripe_fee_cents, r.net_eligible_cents, r.commission_cents,
        r.stripe_customer_id, r.stripe_subscription_id, r.stripe_payment_intent_id, r.stripe_charge_id,
        r.stripe_session_id, r.stripe_invoice_id, r.hold_until, r.approved_at, r.paid_at, r.canceled_at,
        r.cancel_reason, r.self_referral, r.environment,
      ].map(esc).join(","));
    }
    return { ok: true as const, csv: lines.join("\n") };
  });

// Fraud/self-referral scan: also flags multiple purchases by same customer_id
// on a single affiliate within a short window (velocity spike).
export const listFraudSignals = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureOwner(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: self } = await supabaseAdmin
      .from("affiliate_commissions")
      .select("id, affiliate_id, affiliate_code, stripe_customer_id, gross_cents, created_at, status")
      .eq("self_referral", true)
      .order("created_at", { ascending: false })
      .limit(200);

    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const { data: recent } = await supabaseAdmin
      .from("affiliate_commissions")
      .select("id, affiliate_id, affiliate_code, stripe_customer_id, gross_cents, created_at, status")
      .gte("created_at", since)
      .limit(500);
    const groups = new Map<string, any[]>();
    for (const r of recent ?? []) {
      if (!r.stripe_customer_id) continue;
      const k = `${r.affiliate_id}::${r.stripe_customer_id}`;
      groups.set(k, [...(groups.get(k) ?? []), r]);
    }
    const velocity: any[] = [];
    for (const [, arr] of groups) if (arr.length >= 3) velocity.push(...arr);

    return { selfReferrals: self ?? [], velocity };
  });

// -------- Public: click tracking + ref resolution --------

export const trackAffiliateClick = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ slug: SlugSchema }).parse(d))
  .handler(async ({ data }) => {
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: aff } = await supabaseAdmin
        .from("affiliates")
        .select("id, code, discount_percent, active, starts_at, ends_at")
        .eq("ref_slug", data.slug.toUpperCase())
        .maybeSingle();
      if (!aff || !aff.active) return { ok: false as const };
      const now = Date.now();
      if (aff.starts_at && new Date(aff.starts_at).getTime() > now) return { ok: false as const };
      if (aff.ends_at && new Date(aff.ends_at).getTime() < now) return { ok: false as const };
      await supabaseAdmin.from("affiliate_events").insert({
        affiliate_id: aff.id,
        kind: "click",
      });
      return { ok: true as const, code: aff.code, discount_percent: aff.discount_percent };
    } catch (e) {
      console.error("trackAffiliateClick failed", e);
      return { ok: false as const };
    }
  });

// -------- Affiliate self-service dashboard --------
// Returns only stats/data scoped to the currently authenticated affiliate
// (matched by email). NEVER exposes Routify profit, internal costs, VAT,
// Stripe fees, AI/server costs, or any customer PII.

export const getMyAffiliateDashboard = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Prefer direct account link; fall back to matching email (also auto-links).
    let aff: any = null;
    {
      const { data } = await supabaseAdmin
        .from("affiliates")
        .select("id, name, code, ref_slug, discount_percent, commission_percent, active, starts_at, ends_at, created_at, user_id")
        .eq("user_id", context.userId)
        .maybeSingle();
      aff = data;
    }
    if (!aff) {
      const { data: userRes } = await supabaseAdmin.auth.admin.getUserById(context.userId);
      const email = userRes?.user?.email?.toLowerCase();
      if (!email) return { ok: false as const, error: "no_email" };
      const { data } = await supabaseAdmin
        .from("affiliates")
        .select("id, name, code, ref_slug, discount_percent, commission_percent, active, starts_at, ends_at, created_at, user_id")
        .eq("email", email)
        .maybeSingle();
      aff = data;
      if (aff && !aff.user_id) {
        await supabaseAdmin
          .from("affiliates")
          .update({ user_id: context.userId, updated_at: new Date().toISOString() })
          .eq("id", aff.id);
      }
    }
    if (!aff) return { ok: false as const, error: "not_an_affiliate" };


    // Aggregate events (clicks / signups / purchases)
    const [clicks, signups, purchases, commissionsRes] = await Promise.all([
      supabaseAdmin.from("affiliate_events").select("id", { count: "exact", head: true }).eq("affiliate_id", aff.id).eq("kind", "click"),
      supabaseAdmin.from("affiliate_events").select("id", { count: "exact", head: true }).eq("affiliate_id", aff.id).eq("kind", "signup"),
      supabaseAdmin.from("affiliate_events").select("id", { count: "exact", head: true }).eq("affiliate_id", aff.id).eq("kind", "purchase"),
      supabaseAdmin
        .from("affiliate_commissions")
        .select("id, created_at, status, currency, gross_cents, commission_cents, approved_at, paid_at, canceled_at, hold_until")
        .eq("affiliate_id", aff.id)
        .order("created_at", { ascending: false })
        .limit(200),
    ]);

    const rows = (commissionsRes.data ?? []) as Array<{
      id: string;
      created_at: string;
      status: string;
      currency: string;
      gross_cents: number;
      commission_cents: number;
      approved_at: string | null;
      paid_at: string | null;
      canceled_at: string | null;
      hold_until: string | null;
    }>;

    const sum = (pred: (s: string) => boolean, field: "commission_cents" | "gross_cents") =>
      rows.filter((r) => pred(r.status)).reduce((a, r) => a + (r[field] ?? 0), 0);

    const clicksCount = clicks.count ?? 0;
    const signupsCount = signups.count ?? 0;
    const purchasesCount = purchases.count ?? 0;

    const eligibleRows = rows.filter((r) => r.status !== "canceled" && r.status !== "void");
    const totalSalesCents = eligibleRows.reduce((a, r) => a + (r.gross_cents ?? 0), 0);
    const pendingCents = sum((s) => s === "pending" || s === "validating", "commission_cents");
    const approvedCents = sum((s) => s === "approved", "commission_cents");
    const paidCents = sum((s) => s === "paid", "commission_cents");

    const conversionRate = clicksCount > 0 ? purchasesCount / clicksCount : 0;

    return {
      ok: true as const,
      affiliate: {
        name: aff.name,
        code: aff.code,
        ref_slug: aff.ref_slug,
        discount_percent: Number(aff.discount_percent ?? 0),
        commission_percent: Number(aff.commission_percent ?? 0),
        active: aff.active,
        starts_at: aff.starts_at,
        ends_at: aff.ends_at,
        created_at: aff.created_at,
      },
      stats: {
        clicks: clicksCount,
        visits: clicksCount, // visits attributed == tracked clicks with valid ref
        signups: signupsCount,
        purchases: purchasesCount,
        totalSalesCents,
        pendingCents,
        approvedCents,
        paidCents,
        conversionRate,
        currency: rows[0]?.currency ?? "EUR",
      },
      commissions: rows.map((r) => ({
        id: r.id,
        created_at: r.created_at,
        status: r.status,
        currency: r.currency,
        gross_cents: r.gross_cents,
        commission_cents: r.commission_cents,
        approved_at: r.approved_at,
        paid_at: r.paid_at,
        canceled_at: r.canceled_at,
        hold_until: r.hold_until,
      })),
    };
  });

